import {Alert, Box, Grid, Tooltip, Typography} from '@mui/material';
import Panels from 'components/Panels/Panels';
import BlankCard from 'components/BlankCard/BlankCard';
import {FC} from 'react';
import {useTranslation} from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import './LeavesBalance.scss';
import withLeavesViewModel, {
  LeavesModel,
} from 'viewComponents/LeavesBalance/leavesBalanceViewModel';
type InfoHeaderProps = {
  onClick?: () => void;
  tooltip?: string;
};

const InfoHeader: FC<InfoHeaderProps> = ({onClick, tooltip}) => {
  return (
    <div className="InfoHeader flex justify-end">
      <Tooltip title={tooltip ?? ''}>
        <InfoOutlinedIcon className="cursor-pointer" onClick={onClick} />
      </Tooltip>
    </div>
  );
};
interface DetailCardProps {
  title: number;
  description: string;
  tooltip?: string;
}
const DetailCard: FC<DetailCardProps> = ({title, description, tooltip}) => {
  return (
    <BlankCard
      wrapperProps={{
        className: 'flex flex-col text-center justify-center items-center',
      }}
      className="h-full"
      title={<InfoHeader tooltip={tooltip} />}
    >
      <Typography variant="h4">{title}</Typography>
      <Typography variant="caption">{description}</Typography>
    </BlankCard>
  );
};

export interface LeavesBalanceProps {
  balance?: LeavesModel;
}

export const LeavesBalance: FC<LeavesBalanceProps> = ({balance}) => {
  const {t} = useTranslation();
  const {
    availableDays,
    vacationDays,
    additionalLeaveDays,
    daysToExpire,
    yearsOfService,
    accumulatedDays,
    usedDays,
  } = balance || {};

  return (
    <Box
      className="LeavesBalance"
      sx={{
        flex: 'flex',
        flexFlow: 'row',
        justifyContent: 'stretch',
        alignItems: 'center',
      }}
    >
      <BlankCard
        className="p-0"
        title={<InfoHeader tooltip={t('leaves.available_days_tooltip')} />}
      >
        <Panels
          panelProps={{
            className: 'h-full',
          }}
          items={[
            <div
              key="available"
              className="flex flex-col justify-center items-center"
            >
              <Typography variant="h2">
                {availableDays ? Math.floor(availableDays * 100) / 100 : '0'}
              </Typography>
              <Typography variant="caption">
                {t('leaves.available_days_label')}
              </Typography>
            </div>,
            <div key="used" className="flex h-full flex-col justify-between">
              <div className="flex items-center">
                <Typography variant="h5">
                  {vacationDays ? Math.floor(vacationDays * 100) / 100 : 0}
                </Typography>
                <Typography variant="caption">
                  {t('leaves.vacation_days_label')}
                </Typography>
              </div>
              <div className="flex items-center">
                <Typography variant="h5">
                  {Math.floor((additionalLeaveDays ?? 0) * 100) / 100}
                </Typography>
                <Typography variant="caption">
                  {t('leaves.additional_days_label')}
                </Typography>
              </div>
            </div>,
          ]}
        />
        <hr className="my-2" />
        {(daysToExpire || []).map(message => (
          <Alert
            key={message}
            severity="warning"
            icon={<InfoIcon />}
            className="mt-2"
            sx={{alignItems: 'center'}}
          >
            {message}
          </Alert>
        ))}
      </BlankCard>
      <Grid
        className="mt-4"
        container
        columns={13}
        sx={{justifyContent: 'space-between', alignItems: 'stretch'}}
      >
        <Grid item xs={4}>
          <DetailCard
            title={yearsOfService ?? 0}
            description={t('leaves.years_of_service_label')}
            tooltip={t('leaves.years_of_service_tooltip')}
          />
        </Grid>
        <Grid item xs={4}>
          <DetailCard
            title={
              accumulatedDays ? Math.floor(accumulatedDays * 100) / 100 : 0
            }
            description={t('leaves.accumulated_days_label')}
            tooltip={t('leaves.accumulated_days_tooltip')}
          />
        </Grid>
        <Grid item xs={4}>
          <DetailCard
            title={usedDays ?? 0}
            description={t('leaves.used_days_label')}
            tooltip={t('leaves.used_days_tooltip')}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default withLeavesViewModel()(LeavesBalance);
